import { useState, useEffect, useCallback, createContext, useContext } from 'react';

const THEME_KEY = 'pengji_theme';

const defaultTheme = {
  primary: '#FF6B1A',
  primaryLight: '#FF8A3D',
  primaryDark: '#E0550D',
  bubbleShape: 'rounded',
  bubbleMineColor: '#FF6B1A',
  bubbleOtherColor: '#ffffff',
  fontSize: 'medium',
  chatBackground: '#FAFAFA',
  elderMode: false,
  elderFontPx: 20,
};

const ThemeContext = createContext(null);

function applyThemeToDom(theme) {
  const root = document.documentElement;
  root.style.setProperty('--theme-primary', theme.primary);
  root.style.setProperty('--theme-primary-light', theme.primaryLight);
  root.style.setProperty('--theme-primary-dark', theme.primaryDark);
  root.style.setProperty('--theme-bubble-mine', theme.bubbleMineColor);
  root.style.setProperty('--theme-bubble-other', theme.bubbleOtherColor);

  const elderPx = theme.elderFontPx || 20;
  const basePx =
    theme.elderMode
      ? elderPx
      : theme.fontSize === 'small'
        ? 13
        : theme.fontSize === 'large'
          ? 16
          : 14;
  root.style.setProperty('--theme-font-size', `${basePx}px`);

  document.documentElement.setAttribute('data-bubble-shape', theme.bubbleShape);
  document.documentElement.setAttribute('data-font-size', theme.elderMode ? 'elder' : theme.fontSize);
  document.documentElement.classList.toggle('elder-mode', !!theme.elderMode);
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      return saved ? { ...defaultTheme, ...JSON.parse(saved) } : defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  useEffect(() => {
    applyThemeToDom(theme);
    localStorage.setItem(THEME_KEY, JSON.stringify(theme));
  }, [theme]);

  const updateTheme = useCallback((partial) => {
    setTheme((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetTheme = useCallback(() => {
    setTheme(defaultTheme);
  }, []);

  const syncElderFromServer = useCallback((user) => {
    if (!user) return;
    const elderMode = !!(user.elderMode ?? user.preferences?.elderMode);
    setTheme((prev) => {
      if (prev.elderMode === elderMode) return prev;
      return { ...prev, elderMode };
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, resetTheme, syncElderFromServer }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

export const PRESET_THEMES = [
  { name: '默认橙', primary: '#FF6B1A', label: '默认' },
  { name: '深海蓝', primary: '#3B82F6', label: '深海' },
  { name: '翡翠绿', primary: '#10B981', label: '翡翠' },
  { name: '紫罗兰', primary: '#8B5CF6', label: '紫罗兰' },
  { name: '玫瑰金', primary: '#EC4899', label: '玫瑰' },
  { name: '暗夜', primary: '#6B7280', label: '暗夜' },
];

export const BUBBLE_SHAPES = [
  { value: 'rounded', label: '圆角', className: 'rounded-2xl' },
  { value: 'square', label: '直角', className: 'rounded-lg' },
  { value: 'round', label: '全圆', className: 'rounded-3xl' },
];

export const FONT_SIZES = [
  { value: 'small', label: '小', className: 'text-xs' },
  { value: 'medium', label: '中', className: 'text-sm' },
  { value: 'large', label: '大', className: 'text-base' },
];
